import {AfterContentInit, Component, EventEmitter, OnDestroy, ViewChild} from '@angular/core';
import {combineLatest, Subscription}                                     from 'rxjs';
import {ApGridPagerType, IGridData, IWizardChoiceData}                   from '../../../ap-interface';
import {ApPermissions}                                                   from 'invoker-transport';
import {
  ApMachinesLoggerEditComponent
}                                                                        from '../../../entry-components/ap-settings-entry-components/ap-machines-logger-edit.component';
import {
  ApDynComponentComponent
}                                                                        from '../../../ap-dyncomponent/ap-dyncomponent.component';
import {
  ApDynGridColumnConfigBase
}                                                                        from '../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApDynGridPagerConfigBase
}                                                                        from '../../../ap-dyngrids/config/ap-dyn-grid-pager-config-base';
import {
  ApDynGridStatusColumnConfig
}                                                                        from '../../../ap-dyngrids/config/ap-dyn-grid-status-column-config';
import {
  ApDynGridPropertyColumnConfig
}                                                                        from '../../../ap-dyngrids/config/ap-dyn-grid-property-column-config';
import {
  ApDynGridEditColumnConfig
}                                                                        from '../../../ap-dyngrids/config/ap-dyn-grid-edit-column-config';
import {
  ApDynGridPagerConfig
}                                                                        from '../../../ap-dyngrids/config/ap-dyn-grid-pager-config';
import {
  SettingsStore
}                                                                        from '../../../stores/base-data/settings.store';
import {
  AccessPermissionsStore
}                                                                        from '../../../stores/settings/access.permission.store';
import {
  MachineLoggerStore
}                                                                        from '../../../stores/docu/machine.logger.store';
import {filter, map}                                                     from 'rxjs/operators';
import {FarmUsersStore}                                                  from '../../../stores/farm/farm.users.store';
import {FormStore}                                                       from '../../../stores/layout/form.store';
import {
  TranslationStore
}                                                                        from '../../../stores/translation/translation.store';
import {
  GetPermissionPipe
}                                                                        from '../../../ap-permission/pipes/get-permission.pipe';
import {
  LanguageStore
}                                                                        from '../../../stores/translation/language.store';
import {
  ApDateService
}                                                                        from '../../../ap-core/services/ap-date-service';
import IMachinesLogger = Data.DocuContext.MachinesLogger.IMachinesLogger;

interface IMachineLoggerGridData extends IGridData<IMachinesLogger> {
  Status: number;
  StatusClass: string;
  StatusDesc: string;
}

/**
 * Component for the machines - logger overview
 */
@Component({
  selector: 'ap-machines-logger-overview',
  template: `
    <ap-dyncomponent [columns]="columns"
                     [items]="items$ | async"
                     [loading$]="machinesLoggerLoading$"
                     [pager]="pager"
                     [groupable]="true"
                     [canSearch]="true"
                     (edit)="onEdit($event)"></ap-dyncomponent>`
})

/**
 * Component for the machines - logger overview
 */
export class ApMachinesLoggerComponent implements AfterContentInit, OnDestroy {
  @ViewChild(ApDynComponentComponent, {static: true}) dynComponent: ApDynComponentComponent;
  columns: ApDynGridColumnConfigBase[] = [];
  public machinesLoggerLoading$ = this.machinesLoggerStore.Listen(s => s.loading);
  public items$ = this.machinesLoggerStore.Listen(s => s.data).pipe(
    filter(d => !!d),
    map(d => d.Convert<IMachineLoggerGridData>(ml => {
        const from = this.dateService.getDateMidnight(this.dateService.toFarmDateFromUtcGetJsDate(ml.DateFrom));
        const to = this.dateService.getDateMidnight(this.dateService.toFarmDateFromUtcGetJsDate(ml.DateTo));
        return {
          Id: ml.Id,
          SourceItem: ml,
          Status: ml.IsDeleted ? -1 : 1,
          StatusClass: ml.IsDeleted ? 'ap-status-red' : 'ap-status-green',
          StatusDesc: ml.IsDeleted ? 'Global__Inactive' : 'Global__Active',
          From: from,
          To: to,
        };
      })
    ));
  pager: ApDynGridPagerConfigBase;

  private _listMassEditing = [];
  private _subscriptions: Array<Subscription> = [];
  private _applyClicked = new EventEmitter<any>();

  constructor(private dateService: ApDateService,
              private machinesLoggerStore: MachineLoggerStore,
              private formStore: FormStore,
              private translationStore: TranslationStore,
              private farmUsersStore: FarmUsersStore,
              private permissionsStore: AccessPermissionsStore,
              private permissionPipe: GetPermissionPipe,
              private settingsStore: SettingsStore,
              private languageStore: LanguageStore) {
    this._subscriptions.push(this._applyClicked.subscribe(() => this._onApplyClicked()));
  }

  ngAfterContentInit(): void {
    this.getDataFromStore();
    this.gridBuilder();
    this.fillWizardChoiceData();
  }

  getDataFromStore(): void {
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(d => {
      d.unsubscribe();
    });
  }

  onEdit(dataItem): void {
    this.formStore.openForm({component: ApMachinesLoggerEditComponent, data: dataItem.SourceItem});
  }

  private gridBuilder(): void {
    combineLatest([
      this.permissionPipe.transform(ApPermissions.EDIT_LOGGER),
      this.languageStore.SelectedLanguage$
    ]).subscribe(([permission, language]) => {
      this.columns = [
        new ApDynGridStatusColumnConfig({
          field: 'Status', showHeaderTitle: true,
          class: 'StatusClass', description: 'StatusDesc', filterable: true, groupIndex: 0, groupDesc: true
        }),
        new ApDynGridPropertyColumnConfig({
          field: 'SourceItem.Machine.NameWithSign',
          title: 'Docu_Ini__Engine',
          filterable: true,
          width: 100,
          headerFilterable: true
        }),
        new ApDynGridPropertyColumnConfig({
          field: 'SourceItem.Logger.Imei',
          title: 'Docu_Ini__Datalogger',
          filterable: true,
          width: 100,
          headerFilterable: true
        }),
        new ApDynGridPropertyColumnConfig({
          field: 'From',
          title: 'Docu_Ini__From',
          filterable: true,
          width: 100,
          sortIndex: 0,
          sortDesc: false,
          format: language.DateFormat,
          headerFilterable: true
        }),
        new ApDynGridPropertyColumnConfig({
          field: 'To',
          title: 'Docu_Ini__To',
          filterable: true,
          width: 100,
          format: language.DateFormat,
          headerFilterable: true
        }),
        new ApDynGridEditColumnConfig(
          {
            disabled: !permission,
          }
        )
      ];
      this.pager = new ApDynGridPagerConfig({
        listMassEditing: this._listMassEditing,
        apply: this._applyClicked,
        hasPermission: permission,
      });
    });
  }

  private fillWizardChoiceData(): void {
    const deleteLogger = {
      id: 0,
      name: this.translationStore.FindTranslationForSelectedLanguage('Global__Delete'),
      type: ApGridPagerType.None,
      values: null,
    } as IWizardChoiceData;
    this._listMassEditing.push(deleteLogger);
  }

  private _onApplyClicked(): void {
    const rows = this.dynComponent.getSelectedItems();
    rows.forEach(row => {
      const item = row['SourceItem'] as IMachinesLogger;
      if (item) {
        this.machinesLoggerStore.deleteMachinesLogger(item as IMachinesLogger);
      }
    });
  }
}
